import styled from '@emotion/styled';
import { DSField, DSLandingPage } from '@hundred5/design-system';
import { Form, FormikProvider, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { object, string } from 'yup';

import { PromptIfDirty, useDocumentTitle } from '@/features/common';

import { useWorkspaceId } from '@/hooks/router';
import { getCareersPageQueryKey } from '@/queries/careers/keys';
import { usePatchCareersPageMutation } from '@/queries/careers/mutations';
import { useGetCareersPage } from '@/queries/careers/queries';
import { useUploadImageMutation } from '@/queries/workspace/mutations';
import { queryClient } from '@/utils/reactQuery';
import {
  createEditorState,
  CustomTextEditor,
  getEditorMarkdown,
} from '../common/components/custom-text-editor';
import { useNotifications } from '../notifications';
import { useWorkspacePermission } from '../permissions';
import { useWorkspace } from '../workspace';
import { CareersHeader } from './careers-header';
import { CareersAdminOnly } from './components/careers-admin-only';
import { CareersJobOpeningList } from './components/careers-job-opening-list';

const validationSchema = object().shape({
  description: string()
    .required('Please provide a job description!')
    .max(
      6000,
      'Character limit reached. Please try shortening the page content!'
    ),
  backgroundId: string().nullable(),
  backgroundUrl: string().nullable(),
  backgroundPosition: object().shape({
    x: string().required('X position is required'),
    y: string().required('Y position is required'),
  }),
});

export function Careers() {
  useDocumentTitle('Careers');
  const workspaceId = useWorkspaceId();
  const query = useGetCareersPage(Number(workspaceId));
  const { addNotification } = useNotifications();
  const imageMutation = useUploadImageMutation();

  const canReadCareersSettings = useWorkspacePermission()(
    'careers settings',
    'read'
  );
  const workspace = useWorkspace();

  const [editorState, setEditorState] = useState(createEditorState(''));
  const patchCareersPageMutation = usePatchCareersPageMutation();

  useEffect(() => {
    setEditorState(createEditorState(query.data?.description || ''));
  }, [query.data]);

  const handleSubmit = async () => {
    await patchCareersPageMutation.mutateAsync(
      {
        workspaceId: Number(workspaceId),
        data: {
          description: formik.values.description,
          backgroundId: formik.values.backgroundId,
          backgroundPosition: formik.values.backgroundPosition,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getCareersPageQueryKey(Number(workspaceId)),
          });
        },
      }
    );

    // Reset the form and update initial values
    formik.resetForm({ values: formik.values });
    addNotification({ type: 'saved' });
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      backgroundId: undefined,
      backgroundUrl: query.data?.backgroundUrl ?? '',
      backgroundPosition: query.data?.backgroundPosition || { x: 0, y: 0 },
      description: query.data?.description || '',
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  if (!workspace) {
    return null;
  }

  return (
    <>
      <CareersHeader
        onSave={() => formik && formik.handleSubmit()}
        saveDisabled={!formik.dirty}
      />
      {canReadCareersSettings ? (
        <FormikProvider value={formik}>
          <StyledForm>
            <PromptIfDirty />
            <Container>
              <DSLandingPage embedded>
                <DSLandingPage.TitleImage
                  editable
                  url={formik?.values?.backgroundUrl}
                  position={
                    query.data?.backgroundPosition ||
                    formik.values?.backgroundPosition
                  }
                  onUpload={(file) => {
                    imageMutation.mutate(
                      {
                        workspaceId: Number(workspaceId),
                        upload: file,
                      },
                      {
                        onSuccess: (data) => {
                          formik?.setFieldValue('backgroundId', data.id);
                          formik?.setFieldValue('backgroundUrl', data.url);
                        },
                      }
                    );
                  }}
                  onDelete={() => {
                    formik?.setFieldValue('backgroundId', '');
                    formik?.setFieldValue('backgroundUrl', '');
                  }}
                  onReposition={(position) => {
                    formik?.setFieldValue('backgroundPosition', position);
                  }}
                />

                <DSLandingPage.Header>
                  <DSLandingPage.Logo url={workspace.logo_url} />
                </DSLandingPage.Header>

                <DSLandingPage.Main>
                  <SettingsSection>
                    <DSField
                      required
                      for="description"
                      error={
                        formik?.touched.description &&
                        !!formik?.errors.description ? (
                          <div>{formik?.errors.description.toString()}</div>
                        ) : null
                      }
                    >
                      <CustomTextEditor
                        state={editorState}
                        onChange={(state) => {
                          formik?.setFieldValue(
                            'description',
                            getEditorMarkdown(state)
                          );
                          setEditorState(state);
                        }}
                        placeholder="Give a short description about your company here"
                        error={
                          formik?.touched.description &&
                          !!formik?.errors.description
                        }
                      />
                    </DSField>
                  </SettingsSection>

                  <SettingsSection>
                    <CareersJobOpeningList />
                  </SettingsSection>
                </DSLandingPage.Main>
              </DSLandingPage>
            </Container>
          </StyledForm>
        </FormikProvider>
      ) : (
        <CareersAdminOnly />
      )}
    </>
  );
}

const StyledForm = styled(Form)`
  display: flex;
  flex: 1;
`;

const Container = styled.div`
  background-color: ${(props) => props.theme.colors.white};
  padding: 16px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 28px;
  flex: 1;
`;

const SettingsSection = styled.section`
  display: grid;
  gap: 16px;
`;
